/**
 * Type picker for creating a report.
 */
import * as React from 'react';
import { FileText } from 'lucide-react';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { cn } from '@/lib/utils';
import {
  getVisibleRemoteReportCreateTypeOptions as getVisibleCreateTypeOptions,
  REPORT_REMOTE_SELECT_TYPE as SELECT_TYPE,
} from '@/components/reports/report-configs';
import type { ReportTab as Tab } from '@/types/remote/report.types';

/** Props for {@link SelectReportTypeModal}. */
interface SelectReportTypeModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (type: Tab) => void;
  /** Optional list of allowed tab IDs (RBAC-gated). If provided, only shows these types. */
  allowedTabIds?: Tab[];
}

/** Modal to pick a remote report type before create. */
export const SelectReportTypeModal: React.FC<Readonly<SelectReportTypeModalProps>> = ({
  open,
  onClose,
  onSelect,
  allowedTabIds,
}) => {
  const visibleOptions = getVisibleCreateTypeOptions(allowedTabIds);

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      size="lg"
      header={{
        icon: FileText,
        iconSize: 'md',
        title: SELECT_TYPE.title,
        subtitle: SELECT_TYPE.subtitle,
        titleSize: 'lg',
        titleWeight: 'semibold',
      }}
      hideContentWrapper
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
        {visibleOptions.map((option) => {
          const Icon = option.icon;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onSelect(option.id)}
              className={cn(
                'group relative flex flex-col items-start gap-3 rounded-2xl border-2 p-5 text-left transition-all',
                'border-[var(--color-1-nuance-3)] bg-[color-mix(in_srgb,var(--color-1-nuance-3)_3%,transparent)]',
                'hover:-translate-y-0.5 hover:shadow-lg hover:border-[var(--type-accent)]',
                'hover:bg-[color-mix(in_srgb,var(--type-accent)_8%,transparent)]',
                'focus:outline-none focus-visible:ring-2',
              )}
              style={{ '--type-accent': option.accentVar } as React.CSSProperties}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border-2 border-[var(--type-accent)] bg-[color-mix(in_srgb,var(--type-accent)_10%,transparent)] transition-all">
                <Icon className="h-6 w-6 text-[var(--type-accent)]" />
              </div>
              <div>
                <h3
                  className="text-base font-semibold"
                  style={{ color: 'var(--color-1-nuance-1)' }}
                >
                  {option.title}
                </h3>
                <p className="mt-1 text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  {option.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </BaseModal>
  );
};
